/**
 * Baseline + revision planning APIs (create/approve, draft workflow, history).
 */
import axios from 'axios';
import { notifications } from '@mantine/notifications';
import { showErrorNotification } from '../utils/utils';
import {
  parsePlanningMutationResponse,
  transactionAxios,
  type PlanningAsyncJobHandle,
} from './schedulingApis';

const successNotificationColor = 'teal';

export type BaselineRevisionStatus = 'DRAFT' | 'SUBMITTED' | 'REJECTED' | 'APPROVED';

/** One entry from `meta.rejectionHistory` on an open draft restored after a reject. */
export interface BaselineRejectionHistoryEntry {
  reviewedAt: string | null;
  reviewedBy: string | null;
  reviewComment: string | null;
  revisionNumber: number | null;
}

export interface BaselineRevisionMeta {
  rejectionHistory: BaselineRejectionHistoryEntry[];
  previousRevisionNumber: number | null;
}

export interface BaselineRevisionSummary {
  id: string;
  projectId: string;
  revisionNumber: number;
  baselineIndex: number;
  baselineName: string | null;
  status: BaselineRevisionStatus;
  createdBy?: string | null;
  createdAt?: string | null;
  reviewedBy?: string | null;
  reviewedAt?: string | null;
  reviewComment?: string | null;
  taskCount?: number | null;
  /** Soft-deleted on the server — hide from Baselines list; disable in Compare dropdown. */
  isDeleted?: boolean;
  /** Present when the open draft carries prior rejection context from the server. */
  meta?: BaselineRevisionMeta | null;
}

export interface BaselineRevisionTaskRow {
  taskId: string;
  taskUniqueId?: string | null;
  taskName: string | null;
  start: string | null;
  finish: string | null;
  duration: number | null;
  work?: number | null;
  cost?: number | null;
}

export interface BaselineRevisionDetail {
  summary: BaselineRevisionSummary;
  tasks: BaselineRevisionTaskRow[];
}

export type CreateProjectBaselineResponse = PlanningAsyncJobHandle;

export interface CreateProjectBaselineRequest {
  projectId: string;
  baselineIndex?: number;
  baselineName?: string;
}

export interface CreateBaselineRevisionDraftRequest {
  projectId: string;
  baselineIndex?: number;
  baselineName?: string;
}

export interface CreateBaselineRevisionDraftResult {
  revisionId: string;
  revisionNumber: number;
  baselineIndex: number;
  taskCount: number;
  status: BaselineRevisionStatus;
}

export interface DiscardBaselineRevisionRequest {
  projectId: string;
  baselineIndex: number;
  /** Omit to discard the full baseline slot; provide to delete DRAFT/REJECTED revision only. */
  revisionNumber?: number;
}

export interface ApproveBaselineRevisionRequest {
  revisionId: string;
  setAsEarnedValueBaseline?: boolean;
  reviewComment?: string;
}

export interface RejectBaselineRevisionRequest {
  revisionId: string;
  reviewComment?: string;
}

export interface FetchBaselineRevisionHistoryParams {
  projectId: string;
  baselineIndex?: number;
  revisionNumber?: number;
  status?: BaselineRevisionStatus | string;
}

export type BaselineRevisionHistoryResult =
  | { kind: 'detail'; detail: BaselineRevisionDetail }
  | { kind: 'list'; revisions: BaselineRevisionSummary[] };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function isEmptyEnvelopeValue(value: unknown): boolean {
  if (value == null || value === '') return true;
  if (Array.isArray(value)) return value.length === 0;
  const record = asRecord(value);
  if (!record) return false;
  return Object.keys(record).length === 0;
}

/** Parse JSON strings; unwrap common `{ data }` / `{ payload }` envelopes (possibly nested). */
function coerceJsonValue(value: unknown, depth = 0): unknown {
  if (depth > 4) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return coerceJsonValue(JSON.parse(trimmed), depth + 1);
      } catch {
        return value;
      }
    }
    return value;
  }
  const record = asRecord(value);
  if (!record) return value;

  // Prefer the first non-empty structured wrapper key.
  for (const key of ['data', 'payload', 'result', 'body']) {
    if (!(key in record) || isEmptyEnvelopeValue(record[key])) continue;
    return coerceJsonValue(record[key], depth + 1);
  }
  return value;
}

function unwrapEnvelopeData(payload: unknown): unknown {
  return coerceJsonValue(payload);
}

function withSlotHints(
  row: Record<string, unknown>,
  slotIndex?: number | null,
  slotName?: string | null,
): Record<string, unknown> {
  return {
    ...row,
    ...(slotIndex != null &&
    readNumber(row, ['baselineIndex', 'baseline_index', 'index', 'slot']) == null
      ? { baselineIndex: slotIndex }
      : {}),
    ...(slotName && !readString(row, ['baselineName', 'baseline_name', 'name'])
      ? { baselineName: slotName }
      : {}),
  };
}

function readString(source: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return null;
}

function readNumber(source: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return null;
}

function readBoolean(source: Record<string, unknown>, keys: string[]): boolean | null {
  for (const key of keys) {
    if (!(key in source)) continue;
    const value = source[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') {
      if (value === 1) return true;
      if (value === 0) return false;
    }
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      if (normalized === 'true' || normalized === '1' || normalized === 'yes') return true;
      if (normalized === 'false' || normalized === '0' || normalized === 'no') return false;
    }
  }
  return null;
}

function normalizeStatus(raw: string | null): BaselineRevisionStatus | null {
  if (!raw) return null;
  const upper = raw.trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (upper === 'DRAFT' || upper === 'SUBMITTED' || upper === 'REJECTED' || upper === 'APPROVED') {
    return upper;
  }
  // Occasional aliases from gateways
  if (upper === 'PENDING_APPROVAL' || upper === 'PENDING') return 'SUBMITTED';
  if (upper === 'ACTIVE' || upper === 'CURRENT') return 'APPROVED';
  return null;
}

function looksLikeRevisionRow(row: Record<string, unknown>): boolean {
  return (
    readNumber(row, ['revisionNumber', 'revision_number', 'revNo', 'rev_no']) != null ||
    readString(row, ['revisionId', 'revision_id', 'id']) != null
  ) && (
    readNumber(row, ['baselineIndex', 'baseline_index', 'index', 'slot']) != null ||
    normalizeStatus(readString(row, ['status', 'revisionStatus', 'revision_status'])) != null
  );
}

function parseRejectionHistoryEntry(raw: unknown): BaselineRejectionHistoryEntry | null {
  const row = asRecord(raw);
  if (!row) return null;
  return {
    reviewedAt: readString(row, ['reviewedAt', 'reviewed_at']),
    reviewedBy: readString(row, ['reviewedBy', 'reviewed_by']),
    reviewComment: readString(row, ['reviewComment', 'review_comment']),
    revisionNumber: readNumber(row, ['revisionNumber', 'revision_number']),
  };
}

function parseBaselineRevisionMeta(raw: unknown): BaselineRevisionMeta | null {
  const row = asRecord(raw);
  if (!row) return null;

  const historyRaw = row.rejectionHistory ?? row.rejection_history;
  const rejectionHistory: BaselineRejectionHistoryEntry[] = [];
  if (Array.isArray(historyRaw)) {
    for (const item of historyRaw) {
      const entry = parseRejectionHistoryEntry(item);
      if (entry) rejectionHistory.push(entry);
    }
  }

  const previousRevisionNumber = readNumber(row, [
    'previousRevisionNumber',
    'previous_revision_number',
  ]);

  if (rejectionHistory.length === 0 && previousRevisionNumber == null) {
    return null;
  }

  return { rejectionHistory, previousRevisionNumber };
}

/** Newest rejection entry by `reviewedAt`, falling back to the last array item. */
export function latestRejectionHistoryEntry(
  meta?: BaselineRevisionMeta | null,
): BaselineRejectionHistoryEntry | null {
  const history = meta?.rejectionHistory;
  if (!history || history.length === 0) return null;

  let best: BaselineRejectionHistoryEntry | null = null;
  let bestTs = Number.NEGATIVE_INFINITY;
  for (const entry of history) {
    const ts = entry.reviewedAt ? Date.parse(entry.reviewedAt) : Number.NaN;
    if (Number.isFinite(ts) && ts >= bestTs) {
      bestTs = ts;
      best = entry;
    }
  }
  return best ?? history[history.length - 1] ?? null;
}

/** Exported for unit-friendly parsing of active / summary rows. */
export function parseBaselineRevisionSummary(raw: unknown): BaselineRevisionSummary | null {
  const row = asRecord(raw);
  if (!row) return null;

  const revisionNumber = readNumber(row, [
    'revisionNumber',
    'revision_number',
    'revNo',
    'rev_no',
  ]);
  const baselineIndex = readNumber(row, [
    'baselineIndex',
    'baseline_index',
    'index',
    'slot',
  ]);
  const status =
    normalizeStatus(readString(row, ['status', 'revisionStatus', 'revision_status'])) ??
    // List rows sometimes omit status for approved history; treat as APPROVED when flagged.
    (row.approved === true || row.isApproved === true ? 'APPROVED' : null);

  if (revisionNumber == null || baselineIndex == null) {
    return null;
  }

  // Status is required for UI grouping actions; default APPROVED for historical rows lacking it.
  const resolvedStatus = status ?? 'APPROVED';

  const id =
    readString(row, ['id', 'revisionId', 'revision_id']) ??
    `baseline-${baselineIndex}-rev-${revisionNumber}`;

  return {
    id,
    projectId: readString(row, ['projectId', 'project_id']) ?? '',
    revisionNumber,
    baselineIndex,
    baselineName: readString(row, ['baselineName', 'baseline_name', 'name']),
    status: resolvedStatus,
    createdBy: readString(row, ['createdBy', 'created_by']),
    createdAt: readString(row, ['createdAt', 'created_at']),
    reviewedBy: readString(row, ['reviewedBy', 'reviewed_by']),
    reviewedAt: readString(row, ['reviewedAt', 'reviewed_at']),
    reviewComment: readString(row, ['reviewComment', 'review_comment']),
    taskCount: readNumber(row, ['taskCount', 'task_count']),
    isDeleted: readBoolean(row, ['isDeleted', 'is_deleted', 'deleted']) ?? false,
    meta: parseBaselineRevisionMeta(row.meta),
  };
}

function parseBaselineRevisionTaskRow(raw: unknown): BaselineRevisionTaskRow | null {
  const row = asRecord(raw);
  if (!row) return null;
  const taskId = readString(row, ['taskId', 'task_id', 'id']);
  if (!taskId) return null;
  return {
    taskId,
    taskUniqueId: readString(row, ['taskUniqueId', 'task_unique_id', 'uniqueId']),
    taskName: readString(row, ['taskName', 'task_name', 'name']),
    start: readString(row, ['start', 'startDate', 'start_date']),
    finish: readString(row, ['finish', 'finishDate', 'finish_date', 'end']),
    duration: readNumber(row, ['duration']),
    work: readNumber(row, ['work', 'workPlanned', 'work_planned']),
    cost: readNumber(row, ['cost', 'costPlanned', 'cost_planned']),
  };
}

/**
 * Walk history payloads of several shapes:
 * - flat revision rows
 * - detail wrappers `{ summary, tasks }`
 * - slot groups `{ baselineIndex, revisions: [...] }`
 * - maps keyed by baselineIndex `{ "0": [...], "3": { summary, tasks } }`
 */
function collectSummariesFromUnknown(
  value: unknown,
  into: BaselineRevisionSummary[],
  slotIndex?: number | null,
  slotName?: string | null,
): void {
  if (value == null) return;

  if (Array.isArray(value)) {
    for (const item of value) collectSummariesFromUnknown(item, into, slotIndex, slotName);
    return;
  }

  const row = asRecord(value);
  if (!row) return;

  // Detail wrapper used both for single-revision GET and for list entries.
  const nestedSummary = asRecord(row.summary);
  if (nestedSummary) {
    const parsed = parseBaselineRevisionSummary(
      withSlotHints(nestedSummary, slotIndex, slotName),
    );
    if (parsed) {
      into.push(parsed);
      return;
    }
  }

  // Slot group: { baselineIndex, baselineName, revisions: [...] }
  if (Array.isArray(row.revisions)) {
    const groupIndex =
      readNumber(row, ['baselineIndex', 'baseline_index', 'index', 'slot']) ?? slotIndex;
    const groupName =
      readString(row, ['baselineName', 'baseline_name', 'name']) ?? slotName;
    collectSummariesFromUnknown(row.revisions, into, groupIndex, groupName);
    return;
  }

  for (const listKey of ['items', 'baselines', 'history', 'revisions', 'summaries'] as const) {
    if (Array.isArray(row[listKey])) {
      collectSummariesFromUnknown(row[listKey], into, slotIndex, slotName);
      return;
    }
  }

  const direct = parseBaselineRevisionSummary(withSlotHints(row, slotIndex, slotName));
  if (direct) {
    into.push(direct);
    return;
  }

  // Grouped map by baselineIndex: { "0": [...], "3": { summary, tasks } }
  // Avoid treating plain summary-like objects as maps when they look like a revision.
  if (looksLikeRevisionRow(row)) {
    return;
  }

  for (const [key, nested] of Object.entries(row)) {
    if (
      key === 'status' ||
      key === 'message' ||
      key === 'requestId' ||
      key === 'errorType' ||
      key === 'refId' ||
      key === 'tasks' ||
      key === 'summary'
    ) {
      continue;
    }
    if (Array.isArray(nested) || asRecord(nested)) {
      const keyIndex = /^\d+$/.test(key) ? Number(key) : null;
      collectSummariesFromUnknown(
        nested,
        into,
        keyIndex ?? slotIndex,
        keyIndex != null ? slotName : slotName,
      );
    }
  }
}

/** Exported for unit-friendly parsing of history / detail envelopes. */
export function parseBaselineRevisionHistoryPayload(
  payload: unknown,
): BaselineRevisionHistoryResult {
  const data = unwrapEnvelopeData(payload);
  const dataObj = asRecord(data);

  // Detail: { summary, tasks }
  if (dataObj && asRecord(dataObj.summary)) {
    const summary = parseBaselineRevisionSummary(dataObj.summary);
    const tasksRaw = Array.isArray(dataObj.tasks) ? dataObj.tasks : [];
    if (summary) {
      return {
        kind: 'detail',
        detail: {
          summary,
          tasks: tasksRaw
            .map(parseBaselineRevisionTaskRow)
            .filter((t): t is BaselineRevisionTaskRow => t != null),
        },
      };
    }
  }

  // Detail-like: revision fields at top level + tasks[]
  if (dataObj && Array.isArray(dataObj.tasks) && looksLikeRevisionRow(dataObj)) {
    const summary = parseBaselineRevisionSummary(dataObj);
    if (summary) {
      return {
        kind: 'detail',
        detail: {
          summary,
          tasks: dataObj.tasks
            .map(parseBaselineRevisionTaskRow)
            .filter((t): t is BaselineRevisionTaskRow => t != null),
        },
      };
    }
  }

  const revisions: BaselineRevisionSummary[] = [];
  collectSummariesFromUnknown(data, revisions);

  // De-dupe by id
  const seen = new Set<string>();
  const unique = revisions.filter((r) => {
    if (seen.has(r.id)) return false;
    seen.add(r.id);
    return true;
  });

  return { kind: 'list', revisions: unique };
}

function parseDraftCreateResult(payload: unknown): CreateBaselineRevisionDraftResult {
  const data = unwrapEnvelopeData(payload);
  const row = asRecord(data);
  if (!row) {
    throw new Error('Draft create response did not include data.');
  }
  const revisionId = readString(row, ['revisionId', 'revision_id', 'id']);
  const revisionNumber = readNumber(row, ['revisionNumber', 'revision_number']);
  const baselineIndex = readNumber(row, ['baselineIndex', 'baseline_index']);
  const taskCount = readNumber(row, ['taskCount', 'task_count']) ?? 0;
  const status = normalizeStatus(readString(row, ['status'])) ?? 'DRAFT';
  if (!revisionId || revisionNumber == null || baselineIndex == null) {
    throw new Error('Draft create response was missing revision identifiers.');
  }
  return { revisionId, revisionNumber, baselineIndex, taskCount, status };
}

function isNotFoundError(err: unknown): boolean {
  if (axios.isAxiosError(err) && err.response?.status === 404) return true;
  if (!axios.isAxiosError(err)) return false;
  const data = asRecord(err.response?.data);
  if (!data) return false;
  const errorType = readString(data, ['errorType', 'error_type']);
  return errorType?.toUpperCase() === 'NOT_FOUND';
}

/**
 * Start project-level baseline creation (auto-approve) from the schedule saved on the server.
 * Omit baselineIndex to use the next free slot. Returns tracking ids for the Request Tracker.
 */
export async function createProjectBaseline(
  request: CreateProjectBaselineRequest,
  options?: { silent?: boolean },
): Promise<CreateProjectBaselineResponse> {
  const body: Record<string, unknown> = { projectId: request.projectId };
  if (request.baselineIndex != null) body.baselineIndex = request.baselineIndex;
  if (request.baselineName != null && request.baselineName.trim() !== '') {
    body.baselineName = request.baselineName.trim();
  }

  try {
    const res = await transactionAxios.post('dpr/apis/planning/baseline', body, {
      headers: { 'Content-Type': 'application/json' },
    });
    const parsed = parsePlanningMutationResponse(res.data);
    if (!parsed.requestId) {
      throw new Error(
        'Baseline creation started but the server did not return a request tracking id.',
      );
    }
    if (!options?.silent) {
      notifications.show({
        title: 'Baseline started',
        message:
          parsed.message ||
          `Request ID: ${parsed.requestId}. Monitor progress in the request tracker.`,
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error creating project baseline: ${err}`);
    showErrorNotification(err, `Sorry! Failed to create project baseline.`);
    throw err;
  }
}

/** GET revision history / detail. */
export async function fetchBaselineRevisionHistory(
  params: FetchBaselineRevisionHistoryParams,
  options?: { silent?: boolean },
): Promise<BaselineRevisionHistoryResult> {
  try {
    const res = await transactionAxios.get('dpr/apis/planning/baseline/revision', {
      params: {
        projectId: params.projectId,
        ...(params.baselineIndex != null ? { baselineIndex: params.baselineIndex } : {}),
        ...(params.revisionNumber != null ? { revisionNumber: params.revisionNumber } : {}),
        ...(params.status != null ? { status: params.status } : {}),
      },
    });
    return parseBaselineRevisionHistoryPayload(res.data);
  } catch (err) {
    console.error(`Error fetching baseline revision history: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch baseline revision history.`);
    }
    throw err;
  }
}

/** POST draft revision from the live schedule. */
export async function createBaselineRevisionDraft(
  request: CreateBaselineRevisionDraftRequest,
  options?: { silent?: boolean },
): Promise<CreateBaselineRevisionDraftResult> {
  const body: Record<string, unknown> = { projectId: request.projectId };
  if (request.baselineIndex != null) body.baselineIndex = request.baselineIndex;
  if (request.baselineName != null && request.baselineName.trim() !== '') {
    body.baselineName = request.baselineName.trim();
  }

  try {
    const res = await transactionAxios.post('dpr/apis/planning/baseline/revision', body, {
      headers: { 'Content-Type': 'application/json' },
    });
    const parsed = parseDraftCreateResult(res.data);
    if (!options?.silent) {
      notifications.show({
        title: 'Draft baseline created',
        message: `Revision ${parsed.revisionNumber} saved as draft (slot ${parsed.baselineIndex}).`,
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error creating baseline revision draft: ${err}`);
    showErrorNotification(err, `Sorry! Failed to create baseline revision draft.`);
    throw err;
  }
}

/** DELETE draft/rejected revision, or discard a full baseline slot when revisionNumber is omitted. */
export async function discardBaselineRevision(
  request: DiscardBaselineRevisionRequest,
  options?: { silent?: boolean },
): Promise<PlanningAsyncJobHandle> {
  const body: Record<string, unknown> = {
    projectId: request.projectId,
    baselineIndex: request.baselineIndex,
  };
  if (request.revisionNumber != null) {
    body.revisionNumber = request.revisionNumber;
  }

  try {
    const res = await transactionAxios.delete('dpr/apis/planning/baseline/revision', {
      data: body,
      headers: { 'Content-Type': 'application/json' },
    });
    const parsed = parsePlanningMutationResponse(res.data);
    if (!options?.silent) {
      notifications.show({
        title: request.revisionNumber != null ? 'Revision discarded' : 'Baseline discarded',
        message: parsed.message || 'Baseline revision was discarded.',
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error discarding baseline revision: ${err}`);
    showErrorNotification(err, `Sorry! Failed to discard baseline revision.`);
    throw err;
  }
}

/**
 * GET open DRAFT / SUBMITTED / REJECTED revision for the project.
 * Returns null when the server responds 404 (no open revision).
 */
export async function fetchActiveBaselineRevision(
  { projectId }: { projectId: string },
  options?: { silent?: boolean },
): Promise<BaselineRevisionSummary | null> {
  try {
    const res = await transactionAxios.get('dpr/apis/planning/baseline/revision/active', {
      params: { projectId },
    });
    const data = unwrapEnvelopeData(res.data);
    return parseBaselineRevisionSummary(data);
  } catch (err) {
    if (isNotFoundError(err)) {
      return null;
    }
    console.error(`Error fetching active baseline revision: ${err}`);
    if (!options?.silent) {
      showErrorNotification(err, `Sorry! Failed to fetch active baseline revision.`);
    }
    throw err;
  }
}

export async function submitBaselineRevision(
  { revisionId }: { revisionId: string },
  options?: { silent?: boolean },
): Promise<PlanningAsyncJobHandle> {
  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/baseline/revision/${encodeURIComponent(revisionId)}/submit`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    const parsed = parsePlanningMutationResponse(res.data);
    if (!options?.silent) {
      notifications.show({
        title: 'Revision submitted',
        message: parsed.message || 'Baseline revision submitted for approval.',
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error submitting baseline revision: ${err}`);
    showErrorNotification(err, `Sorry! Failed to submit baseline revision.`);
    throw err;
  }
}

export async function approveBaselineRevision(
  request: ApproveBaselineRevisionRequest,
  options?: { silent?: boolean },
): Promise<PlanningAsyncJobHandle> {
  const body: Record<string, unknown> = { revisionId: request.revisionId };
  if (request.setAsEarnedValueBaseline != null) {
    body.setAsEarnedValueBaseline = request.setAsEarnedValueBaseline;
  }
  if (request.reviewComment != null && request.reviewComment.trim() !== '') {
    body.reviewComment = request.reviewComment.trim();
  }

  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/baseline/revision/${encodeURIComponent(request.revisionId)}/approve`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    const parsed = parsePlanningMutationResponse(res.data);
    if (!options?.silent) {
      notifications.show({
        title: 'Revision approved',
        message: parsed.message || 'Baseline revision approved and promoted.',
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error approving baseline revision: ${err}`);
    showErrorNotification(err, `Sorry! Failed to approve baseline revision.`);
    throw err;
  }
}

export async function rejectBaselineRevision(
  request: RejectBaselineRevisionRequest,
  options?: { silent?: boolean },
): Promise<PlanningAsyncJobHandle> {
  const body: Record<string, unknown> = { revisionId: request.revisionId };
  if (request.reviewComment != null && request.reviewComment.trim() !== '') {
    body.reviewComment = request.reviewComment.trim();
  }

  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/baseline/revision/${encodeURIComponent(request.revisionId)}/reject`,
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );
    const parsed = parsePlanningMutationResponse(res.data);
    if (!options?.silent) {
      notifications.show({
        title: 'Revision rejected',
        message: parsed.message || 'Baseline revision was rejected.',
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error rejecting baseline revision: ${err}`);
    showErrorNotification(err, `Sorry! Failed to reject baseline revision.`);
    throw err;
  }
}

export async function refreshBaselineRevision(
  { revisionId }: { revisionId: string },
  options?: { silent?: boolean },
): Promise<PlanningAsyncJobHandle> {
  try {
    const res = await transactionAxios.put(
      `dpr/apis/planning/baseline/revision/${encodeURIComponent(revisionId)}/refresh`,
      {},
      { headers: { 'Content-Type': 'application/json' } },
    );
    const parsed = parsePlanningMutationResponse(res.data);
    if (!options?.silent) {
      notifications.show({
        title: 'Snapshot refreshed',
        message: parsed.message || 'Baseline revision snapshot refreshed from the live schedule.',
        color: successNotificationColor,
      });
    }
    return parsed;
  } catch (err) {
    console.error(`Error refreshing baseline revision: ${err}`);
    showErrorNotification(err, `Sorry! Failed to refresh baseline revision.`);
    throw err;
  }
}
